import type {Size2Like} from "@cm/math"
import type {HalImage, HalImageStats, HalImageView, ImageViewArgs, ReadImageDataArgs, WriteImageDataArgs} from "@cm/hal"
import type {ClearArgs, DrawArgs, ViewTransformArgs} from "@cm/hal/hal-paintable"
import type {WebGl2Context} from "#webgl2/webgl2-context"
import type {WebGl2ImageDescriptor} from "#webgl2/webgl2-image"
import type {ImageViewFactory} from "#webgl2/webgl2-image-view-factory"

export abstract class WebGl2ImageBase implements HalImage {
    readonly isHalImage = true

    // HalEntity
    abstract readonly context: WebGl2Context
    abstract dispose(): void

    // HalImage
    abstract readonly descriptor: WebGl2ImageDescriptor
    abstract readonly stats: HalImageStats

    // HalPaintable
    abstract readonly width: number
    abstract readonly height: number
    abstract readonly forceAlphaToOne: boolean

    // HalImage
    abstract getMipLevelSize(mipLevel: number): Size2Like
    abstract readImageDataFloat(args?: ReadImageDataArgs): Float32Array
    abstract writeImageData(args: WriteImageDataArgs): Promise<void>

    // HalPaintable
    abstract beginDraw(args: DrawArgs): number // returns the number of passes
    abstract beginDrawPass(args: DrawArgs, pass: number): ViewTransformArgs // returns the transform matrix
    abstract endDrawPass(args: DrawArgs, pass: number): void
    abstract endDraw(args: DrawArgs): void
    abstract clear(args?: ClearArgs): void

    abstract prepareForUseAsSourceImage(): void

    createView(args: ImageViewArgs): HalImageView {
        const mipLevelOffset = args.mipLevelOffset ?? 0
        // const region = args.region ?? {x: 0, y: 0, ...this.getMipLevelSize(mipLevelOffset)}
        if (!WebGl2ImageBase.viewFactory) {
            throw new Error("Image view factory is not set. Please set it using setViewFactory() before creating a view.")
        }
        return WebGl2ImageBase.viewFactory(this, {
            ...args,
            mipLevelOffset,
        })
    }

    static viewFactory: ImageViewFactory // this is needed to avoid circular dependencies in the imports
}
